import { PreviewAspectRatio, PreviewValidationError, PreviewValidationErrors } from "../models";

import { checkAspectRatioForImage } from "./checkAspectRatioForImage";
import { type VideoDurationRequirement, checkVideoRequirements } from "./checkVideoRequirements";
import { isImage } from "./isImage";
import { isVideo } from "./isVideo";

const maxImgSize = 10_485_760;
const maxVideoSize = 33_554_432;

interface ValidatePreviewFileProps {
    file: File | null;
    url: string;
    fileTypeValidator: (type: string) => boolean;
    formatErrorMsg: PreviewValidationErrors;
    aspectRatio?: PreviewAspectRatio;
    videoDurationRequirement?: VideoDurationRequirement;
}

export const validatePreviewFile = async ({
    file,
    url,
    fileTypeValidator,
    formatErrorMsg,
    aspectRatio,
    videoDurationRequirement,
}: ValidatePreviewFileProps): Promise<void> => {
    if (!file) {
        throw new PreviewValidationError(PreviewValidationErrors.DoesNotExist);
    }
    if (!fileTypeValidator(file.type)) {
        throw new PreviewValidationError(formatErrorMsg);
    }

    if (isImage(file.type) && file.size > maxImgSize) {
        throw new PreviewValidationError(PreviewValidationErrors.FileIsLarge);
    }

    if (isVideo(file.type) && file.size > maxVideoSize) {
        throw new PreviewValidationError(PreviewValidationErrors.FileIsLarge);
    }

    if (isImage(file.type)) {
        return checkAspectRatioForImage(url, aspectRatio);
    }

    if (isVideo(file.type)) {
        // Spectacles (3:4) doesn't support video previews - must select keyframe and crop to image
        if (aspectRatio === PreviewAspectRatio.threeFour) {
            throw new PreviewValidationError(PreviewValidationErrors.VideoIncorrectAspectRatio);
        }
        return checkVideoRequirements(file, videoDurationRequirement);
    }

    throw new PreviewValidationError(PreviewValidationErrors.IncorrectFormat);
};
